<template>
  <div class="page-content">
    <v-chip v-if="isSupportAccount" size="small" color="grey" variant="tonal" class="mb-2">View Only</v-chip>
    <!-- Studio ID display removed -->
    <ReportClipboard ref="clipboard" :report-data="formattedReportData" />
    <h1 class="pb-1"></h1>

    <!-- TABS SECTION -->
    <v-card v-if="!isSatelliteMonitor" class="mx-auto elevation-2 tabs-card">
      <v-container fluid>
        <v-row align="center" justify="space-between">
          <v-tabs v-model="activeTab" class="dashboard-tab">
            <v-tab 
              v-for="tab in tabs" 
              :key="tab.value" 
              :value="tab.value"
              @click="fetchReportData"
            >
              {{ tab.label }}
            </v-tab>
          </v-tabs>
        </v-row>
      </v-container>
    </v-card>

    <!-- SHIFT HEADER SECTION -->
    <v-card class="mt-4 p-3">
      <v-container>
        <v-row align="center">
          <!-- Shift Title (3.5 cols) -->
          <v-col cols="3.5" class="d-flex align-center justify-left">
            <h2 class="shift-title">
              {{ shiftTitle }}
            </h2>
          </v-col>

          <!-- Monitor Name -->
          <v-col cols="3.5">
            <v-text-field
              v-if="isSatelliteMonitor"
              v-model="selectedMonitor"
              label="Monitor"
              variant="outlined"
              hide-details
              readonly
            ></v-text-field>

            <v-combobox
              v-else
              v-model="selectedMonitor"
              :items="studioMonitorOptions"
              label="Monitor"
              variant="outlined"
              hide-details
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              :spellcheck="false"
            ></v-combobox>
          </v-col>

          <!-- Date Picker (4 cols) -->
          <v-col cols="4" class="d-flex align-center justify-center">
            <v-btn icon @click="changeDate(-1)" variant="text" style="color: #2C74B3;">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <v-date-input
              v-model="reportDate"
              label="Date"
              prepend-icon=""
              prepend-inner-icon=""
              hide-details
              variant="outlined"
              @update:model-value="fetchReportData"
            ></v-date-input>

            <v-btn icon @click="changeDate(1)" variant="text" style="color: #2C74B3;">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-col>

          <!-- Color Picker Button (1 col - moved to end) -->
          <v-col cols="1" class="d-flex align-center justify-end" style="margin-left: -18px;">
            <v-btn 
              icon 
              variant="outlined" 
              color="black"
              @click="colorPickerDialog = true"
              size="small"
            >
              <v-icon>mdi-brush</v-icon> 
            </v-btn>

            <!-- Hidden Color Picker Dialog -->
            <v-dialog v-model="colorPickerDialog" max-width="350px" persistent attach="body">
              <v-card>
                <v-card-title class="text-center">Pick a Header Color</v-card-title>
                <v-card-text>
                  <v-color-picker 
                    v-model="headerColor" 
                    hide-inputs
                    show-swatches
                  ></v-color-picker>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn @click="colorPickerDialog = false" color="primary">Done</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>

      </v-container>
    </v-card>

    <!-- MODEL ENTRY FORM -->
    <template v-if="!isSatelliteMonitor">
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <div v-if="modelsList.length === 0">
          No reports available for your studio and shift.
        </div>
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="3">Model Name</v-col>
          <v-col cols="3">Stage Name</v-col>
          <v-col cols="2">
            {{ isSatelliteMonitor ? 'Studio' : 'Room #' }}
          </v-col>
          <v-col cols="3">Assistance</v-col>
          <v-col cols="1" class="d-flex align-center justify-center">
            <v-btn 
              icon 
              variant="outlined" 
              color="white" 
              @click="openStudioModelDialog"
              size="compact"
              style="margin-left: 20px;"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Model Entry Form -->
        <v-form @submit.prevent>
          <v-row v-for="(model, index) in modelsList" :key="index" no-gutters>
            <!-- Model Name -->
            <v-col cols="3">
              <v-text-field
                v-model="model.name"
                variant="outlined"
                hide-details
                density="compact"
                @input="updateObservation(model)"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              ></v-text-field>
            </v-col>

            <!-- Stage Name -->
            <v-col cols="3">
              <v-text-field
                v-model="model.stageName"
                variant="outlined"
                hide-details
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              ></v-text-field>
            </v-col>

            <!-- Room / Studio Column -->
            <v-col cols="2">
              <!-- Satellite: show Studio code (readonly) -->
              <v-text-field
                v-if="isSatelliteMonitor"
                :model-value="getStudioCode(model.studio_id)"
                variant="outlined"
                hide-details
                density="compact"
                readonly
              ></v-text-field>

              <!-- Non-satellite: normal Room dropdown with conflict tooltip -->
              <template v-else>
                <v-tooltip v-if="hasRoomConflict(model)" location="top">
                  <template #activator="{ props }">
                    <v-select
                      v-model="model.room"
                      :items="roomOptions"
                      variant="outlined"
                      hide-details
                      density="compact"
                      clearable
                      v-bind="props"
                      :style="hasRoomConflict(model) ? 'box-shadow: inset 0 0 0 2px red;' : ''"
                      autocomplete="off"
                      autocorrect="off"
                      autocapitalize="off"
                      :spellcheck="false"
                    ></v-select>
                  </template>
                  <span>Multiple models in this room</span>
                </v-tooltip>

                <v-select
                  v-else
                  v-model="model.room"
                  :items="roomOptions"
                  variant="outlined"
                  hide-details
                  density="compact"
                  clearable
                  autocomplete="off"
                  autocorrect="off"
                  autocapitalize="off"
                  :spellcheck="false"
                ></v-select>
              </template>
            </v-col>

            <!-- Assistance Status (Dropdown) -->
            <v-col cols="3">
              <v-combobox
                v-model="model.assistance"
                :items="assistanceOptions"
                variant="outlined"
                hide-details
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              ></v-combobox>
            </v-col>

            <!-- Remove Model Button -->
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-btn icon variant="outlined" color="black" @click="removeModel(index)" size="compact">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <v-dialog v-model="studioModelDialog" max-width="960">
      <v-card class="studio-model-dialog">
        <v-row no-gutters :style="headerStyle" class="studio-selector-header">
          <v-col cols="8">Select Models</v-col>
          <v-col cols="4" class="d-flex justify-end pr-3">
            <v-btn
              variant="text"
              color="white"
              size="small"
              class="studio-selector-manual-btn"
              @click="addManualModelRow"
            >
              Manual Entry
            </v-btn>
          </v-col>
        </v-row>
        <v-divider class="mb-2" style="border-color: white;"></v-divider>
        <v-card-text class="studio-selector-body pt-0">
          <div class="studio-selector-copy">
            Click models to add or remove them from this report. Manual Entry stays available for special cases.
          </div>
          <v-tabs v-model="studioModelDialogTab" color="primary" density="compact" class="studio-selector-tabs">
            <v-tab value="studio">Studio Models</v-tab>
            <v-tab value="satellite">Satellite Models</v-tab>
          </v-tabs>
          <v-window v-model="studioModelDialogTab">
            <v-window-item value="studio">
              <div class="studio-shift-section" v-for="shiftLabel in ['Morning', 'Evening', 'Night']" :key="shiftLabel">
                <div class="studio-shift-title">{{ shiftLabel }}</div>
                <div class="studio-chip-group">
                  <v-chip
                    v-for="model in getStudioModelsByShift(shiftLabel)"
                    :key="`${shiftLabel}-${getReportModelKey(model)}`"
                    size="small"
                    class="studio-model-chip"
                    :style="getStudioChipStyle(model)"
                    @click="toggleStudioModel(model)"
                  >
                    {{ model.stageName || model.fullName || model.name || 'Unnamed Model' }}
                  </v-chip>
                </div>
              </div>
            </v-window-item>
            <v-window-item value="satellite">
              <div class="studio-shift-section">
                <div class="studio-shift-title">{{ studioLabel }} Satellite</div>
                <div class="studio-chip-group">
                  <v-chip
                    v-for="model in getSatelliteModelsByStudio(user?.studio_id)"
                    :key="`studio-satellite-${getSatelliteModelKey(model)}`"
                    size="small"
                    class="studio-model-chip"
                    :style="getSatelliteChipStyle(model)"
                    @click="toggleSatelliteModel(model)"
                  >
                    {{ model.fullName || model.name || 'Unnamed Model' }}{{ model.stageName ? ` · ${model.stageName}` : '' }}
                  </v-chip>
                  <span v-if="getSatelliteModelsByStudio(user?.studio_id).length === 0" class="studio-selector-empty">
                    No active satellite models found for {{ studioLabel }}.
                  </span>
                </div>
              </div>
            </v-window-item>
          </v-window>
        </v-card-text>
      </v-card>
    </v-dialog>
    </template>

    <v-container v-else fluid class="pa-0">
      <div class="satellite-shift-strip mt-4">
        <div class="satellite-shift-strip-title">SHIFT</div>
        <div class="satellite-shift-strip-group">
          <div class="satellite-shift-strip-field">
            <span class="satellite-shift-strip-label">Start</span>
            <TimePicker v-model="satelliteShiftStartTime" :modified="true" :show-continue="false" />
          </div>
          <div class="satellite-shift-strip-field">
            <span class="satellite-shift-strip-label">Finish</span>
            <TimePicker v-model="satelliteShiftEndTime" :modified="true" :show-continue="false" />
          </div>
          <v-btn
            icon
            variant="text"
            density="comfortable"
            class="satellite-shift-strip-toggle"
            :title="hasSecondSatelliteShift ? 'Remove split shift' : 'Add split shift'"
            @click="toggleSatelliteSplitShift()"
          >
            <v-icon>{{ hasSecondSatelliteShift ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
          </v-btn>
        </div>
        <div v-if="hasSecondSatelliteShift" class="satellite-shift-strip-group satellite-shift-strip-group-secondary">
          <div class="satellite-shift-strip-field satellite-shift-strip-field-secondary">
            <span class="satellite-shift-strip-label">Start</span>
            <TimePicker v-model="satelliteSecondShiftStartTime" :modified="true" :show-continue="false" />
          </div>
          <div class="satellite-shift-strip-field satellite-shift-strip-field-secondary">
            <span class="satellite-shift-strip-label">Finish</span>
            <TimePicker v-model="satelliteSecondShiftEndTime" :modified="true" :show-continue="false" />
          </div>
        </div>
      </div>

      <v-card class="p-4 mt-3 satellite-model-dialog">
        <v-row no-gutters :style="headerStyle" class="satellite-selector-header">
          <v-col cols="12">Select Models</v-col>
        </v-row>
        <v-divider class="mb-2" style="border-color: white;"></v-divider>
        <v-card-text class="satellite-selector-body pt-0">
          <v-row>
            <v-col cols="12" md="4" class="satellite-col-cc">
              <div class="satellite-column-title">CC</div>
              <div class="satellite-chip-group">
                <v-chip
                  v-for="model in getSatelliteModelsByStudio(1)"
                  :key="getSatelliteModelKey(model)"
                  size="small"
                  class="satellite-model-chip"
                  :style="getSatelliteChipStyle(model)"
                  @click="toggleSatelliteModel(model)"
                >
                  {{ model.stageName || model.fullName || model.name || 'Unnamed Model' }}
                </v-chip>
              </div>
            </v-col>

            <v-col cols="12" md="4" class="satellite-col-cj">
              <div class="satellite-column-title">CJ</div>
              <div class="satellite-chip-group">
                <v-chip
                  v-for="model in getSatelliteModelsByStudio(2)"
                  :key="getSatelliteModelKey(model)"
                  size="small"
                  class="satellite-model-chip"
                  :style="getSatelliteChipStyle(model)"
                  @click="toggleSatelliteModel(model)"
                >
                  {{ model.stageName || model.fullName || model.name || 'Unnamed Model' }}
                </v-chip>
              </div>
            </v-col>

            <v-col cols="12" md="4" class="satellite-col-bog-pei">
              <div class="satellite-column-title">BOG</div>
              <div class="satellite-chip-group mb-4">
                <v-chip
                  v-for="model in getSatelliteModelsByStudio(3)"
                  :key="getSatelliteModelKey(model)"
                  size="small"
                  class="satellite-model-chip"
                  :style="getSatelliteChipStyle(model)"
                  @click="toggleSatelliteModel(model)"
                >
                  {{ model.stageName || model.fullName || model.name || 'Unnamed Model' }}
                </v-chip>
              </div>

              <div class="satellite-column-title satellite-subgroup-title">PEI</div>
              <div class="satellite-chip-group">
                <v-chip
                  v-for="model in getSatelliteModelsByStudio(4)"
                  :key="getSatelliteModelKey(model)"
                  size="small"
                  class="satellite-model-chip"
                  :style="getSatelliteChipStyle(model)"
                  @click="toggleSatelliteModel(model)"
                >
                  {{ model.stageName || model.fullName || model.name || 'Unnamed Model' }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- TIME TRACKING SECTION -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <div class="time-tracking-review">
          <div class="time-tracking-review-row">
            <span
              class="time-tracking-review-title"
              :class="{ 'is-complete': timeTrackingCompletionPercent === 100 && totalTimeTrackingCount > 0 }"
            >
              Time Tracking Review
            </span>
            <span
              class="time-tracking-review-count"
              :class="{ 'is-complete': timeTrackingCompletionPercent === 100 && totalTimeTrackingCount > 0 }"
            >
              <template v-if="timeTrackingCompletionPercent === 100 && totalTimeTrackingCount > 0">
                All models reviewed
              </template>
              <template v-else>
                {{ reviewedTimeTrackingCount }} / {{ totalTimeTrackingCount }} Reviewed
              </template>
            </span>
          </div>
          <div class="time-tracking-progress-track" aria-hidden="true">
            <div
              class="time-tracking-progress-fill"
              :style="{
                width: `${timeTrackingCompletionPercent}%`,
                backgroundColor: headerColor || '#4caf50'
              }"
            ></div>
          </div>
        </div>

        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="3">Model Name</v-col>
          <v-col cols="2">
            {{ isSatelliteMonitor ? 'Stage Name' : 'Arrival Time' }}
          </v-col>
          <v-col cols="2" class="pl-2">Connection Time</v-col>
          <v-col cols="2" class="pl-2">Log-Out Time</v-col>
          <v-col cols="1.25" class="pl-2">Break</v-col>
          <v-col cols="1.25" class="pl-2">Hours</v-col>
        </v-row>

        <v-divider class="mb-2"></v-divider>

        <!-- Time Entry Form -->
        <v-form @submit.prevent class="time-tracking-form">
          <v-row v-for="(model, index) in timeTrackingModels" :key="index" no-gutters class="time-tracking-row" align="stretch">
            <!-- Model Name -->
            <v-col cols="3">
              <v-text-field 
                v-model="model.name" 
                variant="outlined" 
                hide-details 
                readonly 
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              ></v-text-field>
            </v-col>

            <!-- Arrival Time / Stage Name -->
            <v-col cols="2">
              <v-text-field
                v-if="isSatelliteMonitor"
                :model-value="model.stageName || '-'"
                variant="outlined"
                hide-details
                readonly
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              ></v-text-field>

              <TimePicker
                v-else
                :model-value="model.arrivalTime"
                :modified="model.timeModified?.arrivalTime"
                :modified-color="headerColor"
                :continued="model.timeContinued?.arrivalTime"
                @update:modelValue="handleTimeFieldUpdate(model, 'arrivalTime', $event)"
                @update:continued="handleTimeContinuedUpdate(model, 'arrivalTime', $event)"
              />
            </v-col>

            <!-- Connection Time -->
            <v-col cols="2">
              <TimePicker
                :model-value="model.connectionTime"
                :modified="model.timeModified?.connectionTime"
                :modified-color="headerColor"
                :continued="model.timeContinued?.connectionTime"
                @update:modelValue="handleTimeFieldUpdate(model, 'connectionTime', $event)"
                @update:continued="handleTimeContinuedUpdate(model, 'connectionTime', $event)"
              />
            </v-col>

            <!-- Log-Out Time -->
            <v-col cols="2">
              <TimePicker
                :model-value="model.logoutTime"
                :modified="model.timeModified?.logoutTime"
                :modified-color="headerColor"
                :continued="model.timeContinued?.logoutTime"
                @update:modelValue="handleTimeFieldUpdate(model, 'logoutTime', $event)"
                @update:continued="handleTimeContinuedUpdate(model, 'logoutTime', $event)"
              />
            </v-col>

            <!-- Break -->
            <v-col cols="1.25">
              <TimePicker
                :model-value="model.breakTime"
                :show-ampm="false"
                :modified="isSatelliteMonitor ? true : model.timeModified?.breakTime"
                :modified-color="headerColor"
                @update:modelValue="handleTimeFieldUpdate(model, 'breakTime', $event)"
              />
            </v-col>

            <!-- Online Hours (Auto-Calculated or Manual Override) -->
            <v-col cols="1.25">
              <v-text-field
                v-model="model.manualHours"
                :placeholder="calculateOnlineHours(model)"
                variant="outlined"
                hide-details
                single-line
                density="compact"
                class="time-tracking-compact-field"
                style="text-align: left; padding-left: 0px !important;"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              >
                <template #append-inner>
                  <v-icon
                    size="18"
                    style="cursor: pointer"
                    title="Revert to automatic hours"
                    @click.stop="resetTimeTrackingRow(model)"
                  >
                    mdi-clock-outline
                  </v-icon>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- EARNINGS ENTRY FORM -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <div class="earnings-grid earnings-grid-header" :style="[headerStyle, earningsGridStyle]">
          <div>Stage Name</div>
          <div
            v-for="platform in platforms"
            :key="platform"
            class="earnings-grid-heading"
            :title="platform"
          >
            {{ getPlatformHeaderLabel(platform) }}
          </div>
          <div class="text-center font-weight-bold">Total</div>
        </div>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Earnings Entry Form -->
        <v-form @submit.prevent>
          <div
            v-for="(model, index) in earningsModels"
            :key="index + '-' + model.name"
            class="earnings-grid earnings-grid-row"
            :style="earningsGridStyle"
          >
            <!-- Stage Name (Auto-Filled from First Section) -->
            <div>
              <v-text-field 
                :model-value="model.stageName || '-'" 
                variant="outlined" 
                hide-details 
                readonly
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              ></v-text-field>
            </div>

            <!-- Earnings Inputs for each platform -->
            <div v-for="platform in platforms" :key="platform">
              <v-text-field
                v-model="model.earnings[platform]"
                :value="model.earnings[platform] === 0 ? '' : model.earnings[platform]"
                @blur="handleEmptyEarnings(model, platform)"
                type="number"
                inputmode="decimal"
                variant="outlined"
                density="compact"
                hide-details
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              />
            </div>

            <!-- Row Total (With Background Color Based on Performance) -->
            <div class="font-weight-bold d-flex align-center text-center justify-center">
              <span 
                :class="getEarningsClass(getRowTotal(model.earnings))" 
                class="total-earnings"
              >
                {{ formatCurrency(getRowTotal(model.earnings)) }}
              </span>
            </div>
          </div>

          <!-- Grand Total Row -->
          <v-divider class="mt-3 mb-2" style="border-color: black;"></v-divider>
          <div class="earnings-grid earnings-grid-total" :style="earningsGridStyle">
            <div class="font-weight-bold pl-2">Total Page Earnings</div>
            <div v-for="platform in platforms" :key="platform" class="font-weight-bold text-center">
              {{ formatCurrency(platformTotals[platform]) }}
            </div>
            <div
              class="font-weight-bold d-flex align-center justify-center text-center" 
              style="display: flex; align-items: center;"
            >
              <span 
                :class="getGrandTotalEarningsClass(grandTotal)" 
                class="total-earnings"
                style="margin-top: -8px;"
              >
                {{ formatCurrency(grandTotal) }}
              </span>
            </div>
          </div>
        </v-form>
      </v-card>
    </v-container>

    <!-- ACTIONS & OBSERVATIONS SECTION -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="3">Model Focus</v-col>
          <v-col cols="9">Actions & Observations</v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Dynamic Row for Model Info & Observations -->
        <v-form @submit.prevent>
          <v-row v-for="(model, index) in modelsList" :key="index" no-gutters>
            <v-col cols="3" class="d-flex flex-column justify-space-between">
              <v-text-field 
                v-model="model.name"
                :label="isSatelliteMonitor
                  ? `${model.stageName || '-'} - ${getStudioCode(model.studio_id)}`
                  : `${model.stageName || '-'} - Room ${model.room || '-'}`"
                placeholder="Model Name"
                variant="outlined"
                readonly
                hide-details
                density="compact"
                style="padding-top: 10px;"
                class="mf-focus-field"
                :class="{ 'is-mf': !!model.isModelFocus }"
                :style="{ '--mf-border-color': getModelFocusBorderColor() }"
                @click="setModelFocus(index)"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              ></v-text-field>
            </v-col>
            <v-col cols="9">
              <simple-rich-text-box
                v-model="model.observations"
                class="mf-focus-field"
                :class="{ 'is-mf': !!model.isModelFocus }"
                :accent-color="getModelFocusBorderColor()"
                :style="{ '--mf-border-color': getModelFocusBorderColor() }"
                @change="handleModelFocusObservationInput(index)"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- BREAK & USAGE TRACKING -->
    <v-container v-if="!isSatelliteMonitor" fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col :style="{ flex: 3 }">Break</v-col>
          <v-col :style="{ flex: 2.25, marginLeft: '12px' }">Lush 1</v-col>
          <v-col :style="{ flex: 2.25, marginLeft: '12px' }">Lush 2</v-col>
          <v-col :style="{ flex: 2.25, marginLeft: '12px' }">Sex Machine</v-col>
          <v-col :style="{ flex: 2.25, marginLeft: '12px' }">Cellphone</v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Input Fields -->
        <v-row no-gutters>
          <!-- Break (Regular Input Field) -->
          <v-col cols="3">
            <v-text-field 
              v-model="breakEntry" 
              variant="outlined" 
              hide-details 
              density="compact"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              :spellcheck="false"
            ></v-text-field>
          </v-col>

          <!-- Combobox Inputs for Usage Tracking -->
          <v-col v-for="(field, index) in ['lush1Entry', 'lush2Entry', 'sexMachineEntry', 'cellphoneEntry']" :key="index" cols="2.25">
            <v-combobox 
              v-model="usageEntries[field]" 
              :items="['Monitors Desk', ...modelsList.map(m => m.stageName || m.name)]" 
              variant="outlined" 
              hide-details 
              density="compact"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              :spellcheck="false"
            ></v-combobox>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <!-- ADJUSTMENTS & PURCHASES SECTION -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col :style="{ flex: 3, textAlign: 'start' }">Adjustments</v-col>
          <v-col :style="{ flex: 1.5, textAlign: 'start' }">Type</v-col>
          <v-col :style="{ flex: 1.7, textAlign: 'start' }">Amount ($)</v-col>
          <v-col :style="{ flex: 3.5, textAlign: 'start' }">Description</v-col>
          <v-col :style="{ flex: 0.85, textAlign: 'start' }">Quantity</v-col>
          <v-col :style="{ flex: 0.8, textAlign: 'start' }">Quotes</v-col>
          <v-col :style="{ flex: 0.75 }" class="d-flex align-center justify-center">
            <v-btn icon variant="outlined" color="white" @click="addAdjustmentRow" size="compact" style="margin-left: 22px;">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Dynamic Rows for Adjustments & Purchases -->
        <v-form @submit.prevent>
          <v-row v-for="(entry, index) in adjustmentList" :key="index" no-gutters>
            <v-col :style="{ flex: 3 }">
              <v-combobox
                v-model="entry.name"
                :items="modelNames"
                variant="outlined"
                hide-details
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
                @update:modelValue="handleAdjustmentModelChange(entry, $event)"
              ></v-combobox>
            </v-col>

            <v-col :style="{ flex: 1.5 }">
              <v-select
                v-model="entry.type"
                :items="['Adjustment', 'Discount', 'Purchase', 'Site Bonus', 'Studio Bonus']"
                variant="outlined"
                hide-details
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
                @update:modelValue="handleAdjustmentTypeChange(entry)"
              ></v-select>
            </v-col>

            <v-col :style="{ flex: 1.7 }" class="d-flex align-center">
              <v-text-field
                v-model.number="entry.amount"
                type="number"
                variant="outlined"
                hide-details
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              >
                <template v-slot:append>
                  <span 
                    @click="toggleCurrency(entry)" 
                    :style="{ 
                      cursor: 'pointer', 
                      fontWeight: 'bold', 
                      color: entry.currency === 'USD'
                      ? '#27ae60'
                      : entry.currency === 'COP'
                      ? '#f39c12'
                      : '#2980b9',
                      paddingRight: '12px' 
                    }"
                  >
                    {{ entry.currency }}
                  </span>
                </template>
              </v-text-field>
            </v-col>

            <v-col :style="{ flex: 3.5, minWidth: 0 }">
              <v-menu
                v-if="entry.type === 'Purchase'"
                v-model="entry.inventory_menu_open"
                location="bottom"
                :close-on-content-click="true"
                max-height="280"
              >
                <template #activator="{ props }">
                  <v-text-field
                    v-bind="props"
                    v-model="entry.description"
                    variant="outlined"
                    hide-details
                    density="compact"
                    autocomplete="off"
                    autocorrect="off"
                    autocapitalize="off"
                    :spellcheck="false"
                    @update:modelValue="handleAdjustmentDescription(entry, $event)"
                  >
                    <template #append-inner>
                      <v-icon
                        v-if="entry.inventory_item_id"
                        color="success"
                        size="17"
                        title="Linked to Inventory"
                      >mdi-package-variant-closed</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-list v-if="filteredInventoryOptionsForAdjustment(entry).length" density="compact">
                  <v-list-item
                    v-for="item in filteredInventoryOptionsForAdjustment(entry)"
                    :key="`${item.studio_id}-${item.id}`"
                    :title="item.name"
                    :subtitle="[item.category_name, item.location_name].filter(Boolean).join(' · ')"
                    @click="selectInventoryAdjustmentItem(entry, item)"
                  ></v-list-item>
                </v-list>
                <v-list v-else density="compact">
                  <v-list-item title="No matching inventory item"></v-list-item>
                </v-list>
              </v-menu>
              <v-text-field
                v-else
                v-model="entry.description"
                variant="outlined"
                hide-details
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              ></v-text-field>
            </v-col>

            <v-col :style="{ flex: 0.85, minWidth: '68px' }">
              <v-combobox
                v-model="entry.quantity"
                :items="[1, 2, 3, 4, 5, 6]"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                density="compact"
                hide-details
                inputmode="numeric"
                :spellcheck="false"
                variant="outlined"
                @update:modelValue="handleAdjustmentQuantityChange(entry, $event)"
              ></v-combobox>
            </v-col>

            <v-col :style="{ flex: 0.8 }">
              <v-select
                v-model="entry.quotes"
                :items="['1', '2', '3', '4', '5', '6']"
                variant="outlined"
                hide-details
                density="compact"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                :spellcheck="false"
              ></v-select>
            </v-col>

            <v-col :style="{ flex: 0.75 }" class="d-flex align-center justify-center">
              <v-btn icon variant="outlined" color="black" @click="removeAdjustmentRow(index)" size="compact">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- SHIFT OBSERVATIONS -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="10">Shift Observations</v-col>
          <v-col cols="2" class="d-flex justify-end">
            <v-btn 
              icon 
              variant="outlined" 
              color="white" 
              @click="addObservationRow"
              size="compact"
              style="margin-right: 26px;"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Shift Observations Dynamic Rows -->
        <v-form @submit.prevent>
          <v-row v-for="(observation, index) in observationsList" :key="index" no-gutters align="stretch">
            <v-col cols="11">
              <simple-rich-text-box
                v-model="observationsList[index]"
                :ref="`observation-${index}`"
                class="w-100"
                :accent-color="headerColor"
              />
            </v-col>
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-btn 
                icon 
                variant="outlined" 
                color="black" 
                @click="removeObservationRow(index)"
                size="compact"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <report-pending-tasks
      :tasks="operationalTasks"
      :models="modelsList"
      :header-color="headerColor"
      :report-date="formatDate(reportDate)"
      @refresh="fetchOperationalTasks()"
    />

    <!-- CHECKLIST SECTION -->
    <v-container v-if="!isSatelliteMonitor" fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="12">Checklist</v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Checklist in Two Columns -->
        <v-form @submit.prevent>
          <v-row v-for="(item, index) in checklistItems.slice(0, Math.ceil(checklistItems.length / 2))" :key="index" no-gutters class="mb-2">
            <!-- First Column -->
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-checkbox 
                v-model="checklistItems[index].checked" 
                density="compact"
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col cols="5" class="d-flex align-center">
              {{ checklistItems[index].text }}
            </v-col>

            <!-- Second Column -->
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-checkbox 
                v-if="checklistItems[index + Math.ceil(checklistItems.length / 2)]"
                v-model="checklistItems[index + Math.ceil(checklistItems.length / 2)].checked"
                density="compact"
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col cols="5" class="d-flex align-center">
              <span v-if="checklistItems[index + Math.ceil(checklistItems.length / 2)]">
                {{ checklistItems[index + Math.ceil(checklistItems.length / 2)].text }}
              </span>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- SAVE AND SEND BUTTONS -->
    <v-container fluid class="pa-0 mt-4">
      <v-row justify="end" no-gutters>
        <v-col cols="auto">
          <v-btn 
            color="success" 
            variant="elevated" 
            @click="handleCopyClick"
          >
            Copy
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="showTimeTrackingWarning" max-width="480">
      <v-card class="time-tracking-warning-card">
        <v-card-title class="d-flex align-center" style="gap: 10px;">
          <v-icon :color="headerColor || '#2C74B3'" size="22">mdi-clock-check-outline</v-icon>
          <span>Time Tracking Review</span>
        </v-card-title>
        <v-card-text>
          <div class="time-tracking-warning-box">
            <div class="time-tracking-warning-count">
              {{ incompleteTimeTrackingCount }}
              {{ incompleteTimeTrackingCount === 1 ? 'model still has' : 'models still have' }}
              unreviewed time tracking.
            </div>
            <div class="time-tracking-warning-copy">
              Completing time tracking helps ensure accurate reports and hour calculations.
            </div>
            <div class="time-tracking-warning-muted">
              You can still copy the report now if needed.
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="justify-end" style="gap: 8px;">
          <v-btn variant="text" @click="showTimeTrackingWarning = false">
            Review Time Tracking
          </v-btn>
          <v-btn
            variant="flat"
            :style="{
              backgroundColor: headerColor || '#2C74B3',
              color: '#ffffff'
            }"
            @click="confirmCopyReport"
          >
            Copy Anyway
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TimePicker from '@/components/TimePicker';
import ReportClipboard from '@/components/ReportClipboard.vue';
import ReportPendingTasks from '@/components/ReportPendingTasks.vue';
import SimpleRichTextBox from '@/components/SimpleRichTextBox.vue';
import secureAxios from '@/utils/secureAxios';
import { toast } from 'vue3-toastify';
import { debounce } from 'lodash';

const createAdjustmentSourceId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `report-adjustment-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createEmptyAdjustment = () => ({
  source_id: createAdjustmentSourceId(),
  name: '',
  model_id: null,
  model_studio_id: null,
  inventory_item_id: null,
  inventory_unit_price: null,
  inventory_menu_open: false,
  type: 'Purchase',
  amount: 0,
  currency: 'COP',
  description: '',
  quantity: 1,
  quotes: '1',
});

const bogotaDateTimeParts = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
  };
};

const localReportDate = ({ year, month, day }, dayOffset = 0) => {
  // Noon avoids browser/DST edge cases when the Date is later formatted locally.
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  date.setDate(date.getDate() + dayOffset);
  return date;
};

const DEFAULT_REPORT_SITES = [
  { key: 'flirt4free', displayName: 'Flirt4Free' },
  { key: 'streamate', displayName: 'Streamate' },
  { key: 'livejasmin', displayName: 'LiveJasmin' },
  { key: 'chaturbate', displayName: 'Chaturbate' },
  { key: 'stripchat', displayName: 'StripChat' },
];

const REPORT_SITE_SHORT_LABELS = {
  Flirt4Free: 'F4F',
  Streamate: 'SM',
  LiveJasmin: 'LJ',
  Chaturbate: 'CB',
  StripChat: 'SC',
  Kwiky: 'KW',
  'Sin Party': 'SP',
  'Cams / Streamray': 'Cams',
  Cam4: 'Cam4',
  Bonga: 'Bonga',
};

export default {
  components: {
    TimePicker,
    ReportClipboard,
    ReportPendingTasks,
    SimpleRichTextBox,
  },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user')),
      activeTab: 'morning',
      tabs: [
        { value: 'morning', label: 'Morning' },
        { value: 'evening', label: 'Evening' },
        { value: 'night', label: 'Night' },
      ],
      monitorConfig: {
        morning: '',
        evening: '',
        night: '',
      },
      hasActiveNightMonitor: false,
      initializingReportContext: true,
      selectedMonitor: 'Diana Bolaños',
      monitorManuallyChanged: false,
      reportDate: new Date(),
      headerColor: '#000000',
      colorPickerDialog: false,
      observationsList: [''],
      roomOptions: ['1', '2', '3', '4', '5', '6', 'Other'],
      assistanceOptions: [
        'Yes',
        'No',
        'Day Off',
        'No (Offline Sales)',
        'Period Day',
        'Changed Shift',
        'Permission',
        'Vacation',
      ],
      reportSites: DEFAULT_REPORT_SITES.map(site => ({ ...site })),
      platforms: DEFAULT_REPORT_SITES.map(site => site.displayName),
      modelsList: [],
      operationalTasks: [],
      inventoryItems: [],
      breakEntry: '',
      usageEntries: {
        lush1Entry: 'Monitors Desk',
        lush2Entry: 'Monitors Desk',
        sexMachineEntry: 'Monitors Desk',
        cellphoneEntry: 'Monitors Desk',
      },
      adjustmentList: [
        createEmptyAdjustment(),
      ],
      checklistItems: [
        { text: 'Send Arrival Email', checked: false },
        { text: 'Check Rooms at Start of Shift', checked: false },
        { text: 'Review and Follow the Model Plan', checked: false },
        { text: 'Check Models’ Audio and Video', checked: false },
        { text: 'Log Adjustments: Inventory / Fines', checked: false },
        { text: 'Enter Sales in the Admin Center', checked: false },
        { text: 'Model Focus - Meeting with the Model', checked: false },
        { text: 'Delete RTMP Keys from OBS', checked: false },
        { text: 'Check Rooms and Equipment After Shift', checked: false },
        { text: 'Return Equipment (Lush, Mirrors, Machine, etc.)', checked: false },
        { text: 'Sign the Room Whiteboard', checked: false },
        { text: 'Send Daily Report', checked: false },
        { text: 'Send Exit Email (Include Adjusted Hours)', checked: false },
      ],
      fetchedOnce: false,
      unsavedChanges: false,
      loadingReport: false,
      loadedReportKey: null,
      reportVersion: null,
      reportFetchSequence: 0,
      scheduledFocusIds: [],
      modelFocusManual: false,
      showTimeTrackingWarning: false,
      studioModelDialog: false,
      studioModelDialogTab: 'studio',
      studioModelOptions: [],
      satelliteModelOptions: [],
      satelliteShiftStartTime: { hours: 7, minutes: 0, ampm: 'AM' },
      satelliteShiftEndTime: { hours: 3, minutes: 0, ampm: 'PM' },
      hasSecondSatelliteShift: false,
      satelliteSecondShiftStartTime: { hours: 7, minutes: 0, ampm: 'AM' },
      satelliteSecondShiftEndTime: { hours: 3, minutes: 0, ampm: 'PM' },
    };
  },
  computed: {
    isSatelliteMonitor() {
      return this.user?.role === 'satellite_monitor';
    },
    isSupportAccount() {
      return this.user?.role === 'support';
    },
    shiftTitle() {
      return {
        morning: 'Morning Report',
        evening: 'Evening Report',
        night: 'Night Report',
        satellite: 'Satellite Report',
      }[this.activeTab] || 'Night Shift';
    },
    headerStyle() {
      return {
        backgroundColor: this.headerColor,
        color: 'white',
        padding: '6px 12px',
        fontSize: '14px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        alignItems: 'center',
        height: '40px',
      };
    },
    timeTrackingModels() {
      return this.modelsList.filter((model) => model.assistance === 'Yes');
    },
    reviewedTimeTrackingCount() {
      return this.timeTrackingModels.filter((model) => {
        const modified = model?.timeModified || {};
        if (this.isSatelliteMonitor) {
          return !!(
            modified.connectionTime &&
            modified.logoutTime
          );
        }

        return !!(
          modified.arrivalTime &&
          modified.connectionTime &&
          modified.logoutTime &&
          modified.breakTime
        );
      }).length;
    },
    totalTimeTrackingCount() {
      return this.timeTrackingModels.length;
    },
    hasIncompleteTimeTracking() {
      return this.reviewedTimeTrackingCount < this.totalTimeTrackingCount;
    },
    incompleteTimeTrackingCount() {
      return this.totalTimeTrackingCount - this.reviewedTimeTrackingCount;
    },
    timeTrackingCompletionPercent() {
      if (this.totalTimeTrackingCount === 0) return 0;
      return Math.round((this.reviewedTimeTrackingCount / this.totalTimeTrackingCount) * 100);
    },
    earningsModels() {
      return this.modelsList.filter(
        (model) => model.assistance === 'Yes' || model.assistance === 'No (Offline Sales)'
      );
    },
    modelFocusModels() {
      return this.modelsList.filter(
        (model) => model.observations && model.observations.trim() !== ''
      );
    },
    adjustmentModels() {
      return this.modelsList.filter(
        (model) => model.assistance === 'Yes' || model.assistance === 'No (Offline Sales)'
      );
    },
    platformTotals() {
      let totals = {};
      this.platforms.forEach((platform) => {
        totals[platform] = this.modelsList
          .filter((model) => model.assistance === 'Yes' || model.assistance === 'No (Offline Sales)')
          .reduce((sum, model) => sum + (Number(model.earnings?.[platform]) || 0), 0);
      });
      return totals;
    },
    earningsGridStyle() {
      return {
        gridTemplateColumns: `minmax(130px, 2fr) repeat(${this.platforms.length}, minmax(0, 1fr)) minmax(78px, 0.9fr)`,
      };
    },
    grandTotal() {
      return this.modelsList
        .filter((model) => model.assistance === 'Yes' || model.assistance === 'No (Offline Sales)')
        .reduce((sum, model) => sum + this.getRowTotal(model.earnings), 0);
    },
    modelNames() {
      return [
        ...this.modelsList.map((model) => model.name),
        ...this.studioAccountOptions.map((studio) => studio.name),
      ];
    },
    studioAccountOptions() {
      const studios = [
        { id: 1, name: 'Studio CC' },
        { id: 2, name: 'Studio CJ' },
        { id: 3, name: 'Studio BOG' },
      ];
      if (this.isSatelliteMonitor) return studios;
      return studios.filter(studio => studio.id === Number(this.user?.studio_id));
    },
    formattedReportData() {
      return this.getFormattedReportData();
    },
    studioMonitorOptions() {
      const options = [];
      if (this.monitorConfig?.morning) options.push(this.monitorConfig.morning);
      if (this.monitorConfig?.evening && !options.includes(this.monitorConfig.evening)) {
        options.push(this.monitorConfig.evening);
      }
      if (this.monitorConfig?.night && !options.includes(this.monitorConfig.night)) {
        options.push(this.monitorConfig.night);
      }
      return options;
    },
    studioLabel() {
      const studioMap = {
        1: 'CC',
        2: 'CJ',
        3: 'BOG',
        4: 'PEI'
      };
      return studioMap[this.user?.studio_id] || 'Unknown';
    },
  },
  watch: {
    activeTab: {
      handler(newTab, oldTab) {
        if (this.initializingReportContext) return;
        if (newTab !== oldTab && !this.monitorManuallyChanged) {
          this.selectedMonitor = this.monitorConfig?.[newTab] || '';
        }
        this.unsavedChanges = false;
        this.loadedReportKey = null;
        this.reportVersion = null;
        this.fetchReportData();
      },
    },
    selectedMonitor: {
      handler() {
        if (this.initializingReportContext || this.loadingReport) return;
        this.monitorManuallyChanged = true;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
    },
    reportDate: {
      handler(newDate, oldDate) {
        if (this.initializingReportContext) return;
        if (this.formatDate(newDate) !== this.formatDate(oldDate)) {
          // Changing dates is navigation, not an edit. Do not allow the
          // currently displayed report to autosave into the destination date.
          this.unsavedChanges = false;
          this.loadedReportKey = null;
          this.reportVersion = null;
          this.fetchReportData();
        }
      },
    },
    headerColor: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
    },
    satelliteShiftStartTime: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    satelliteShiftEndTime: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    hasSecondSatelliteShift: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
    },
    satelliteSecondShiftStartTime: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    satelliteSecondShiftEndTime: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    observationsList: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    modelsList: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    breakEntry: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
    },
    usageEntries: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    adjustmentList: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    checklistItems: {
      handler() {
        if (this.loadingReport) return;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
  },
  methods: {
    async fetchActiveReportSites() {
      try {
        const { data } = await secureAxios.get('/api/config/sites');
        const activeSites = Array.isArray(data?.sites)
          ? data.sites.filter(site => site.enabled && site.displayName)
          : [];
        if (activeSites.length > 0) {
          this.reportSites = activeSites.map(site => ({
            key: site.key,
            displayName: site.displayName,
          }));
          this.platforms = this.reportSites.map(site => site.displayName);
        }
      } catch (error) {
        console.warn('Active site configuration unavailable; using the standard report sites.', error?.message);
      }
    },
    getPlatformHeaderLabel(platform) {
      if (this.platforms.length <= 5) return platform;
      return REPORT_SITE_SHORT_LABELS[platform] || platform;
    },
    normalizeEarnings(earnings = {}) {
      return {
        ...this.getEmptyEarnings(),
        ...(earnings && typeof earnings === 'object' ? JSON.parse(JSON.stringify(earnings)) : {}),
      };
    },
    getStudioCode(studioId) {
      const map = {
        1: 'CC',
        2: 'CJ',
        3: 'BOG',
        4: 'PEI',
      };
      return map[Number(studioId)] || '-';
    },
    sortModelsForSatellite() {
      if (!this.isSatelliteMonitor || !Array.isArray(this.modelsList)) return;

      const studioOrder = { 1: 1, 2: 2, 3: 3, 4: 4 };

      this.modelsList.sort((a, b) => {
        const aStudio = studioOrder[Number(a?.studio_id)] ?? 999;
        const bStudio = studioOrder[Number(b?.studio_id)] ?? 999;
        if (aStudio !== bStudio) return aStudio - bStudio;

        const aKey = String(a?.stageName || a?.name || '').toLowerCase();
        const bKey = String(b?.stageName || b?.name || '').toLowerCase();
        return aKey.localeCompare(bKey);
      });
    },
    normalizeModelFocusState(models = []) {
      let focusedFound = false;
      return models.map((model) => {
        const isFocused = !!model?.isModelFocus && !focusedFound;
        if (isFocused) focusedFound = true;
        return { ...model, isModelFocus: isFocused };
      });
    },
    getDeterministicModelFocusIndex(models = []) {
      if (!Array.isArray(models) || models.length === 0) return -1;
      if (!this.modelFocusManual) {
        const scheduled = (this.scheduledFocusIds || []).map(id => models.findIndex(model => {
          const assistance = String(model?.assistance || '').trim().toLowerCase();
          return Number(model.model_id || model.id) === Number(id) && (assistance === '' || assistance === 'yes');
        })).find(index => index >= 0);
        if (scheduled !== undefined) return scheduled;
      }

      const candidates = models
        .map((model, idx) => ({ model, idx }))
        .filter(({ model }) => {
          const assistance = String(model?.assistance || '').trim().toLowerCase();
          if (assistance !== 'yes') return false;
          const name = String(model?.name || '').trim();
          const stageName = String(model?.stageName || '').trim();
          return name !== '' || stageName !== '';
        })
        .map(({ idx }) => idx);

      const pool = candidates;
      if (pool.length === 0) return -1;
      const seed = `${this.formatDate(this.reportDate)}|${this.activeTab || ''}`;

      let hash = 0;
      for (let i = 0; i < seed.length; i++) {
        hash = ((hash << 5) - hash) + seed.charCodeAt(i);
        hash |= 0;
      }

      return pool[Math.abs(hash) % pool.length];
    },
    ensureModelFocusSelection() {
      if (!Array.isArray(this.modelsList) || this.modelsList.length === 0) return;

      const focusedIndex = this.modelsList.findIndex((m) => !!m?.isModelFocus);
      const preferredIndex = this.getDeterministicModelFocusIndex(this.modelsList);
      if (focusedIndex >= 0) {
        const focusedAssistance = String(this.modelsList[focusedIndex]?.assistance || '').trim().toLowerCase();
        const scheduledWithUnsetAttendance = focusedAssistance === '' && (this.scheduledFocusIds || []).some(id => Number(id) === Number(this.modelsList[focusedIndex].model_id || this.modelsList[focusedIndex].id));
        if ((focusedAssistance === 'yes' || scheduledWithUnsetAttendance) && (this.modelFocusManual || !(this.scheduledFocusIds || []).length || focusedIndex === preferredIndex)) return;

        // If currently focused model is no longer "Yes", clear it and repick.
        this.modelsList[focusedIndex].isModelFocus = false;
        this.stripMfMarker(this.modelsList[focusedIndex]);
      }

      const selectedIndex = this.getDeterministicModelFocusIndex(this.modelsList);
      if (selectedIndex < 0) return;

      this.modelsList.forEach((m, i) => {
        m.isModelFocus = i === selectedIndex;
      });
      this.ensureMfMarker(this.modelsList[selectedIndex]);
    },
    createDefaultTimeModified() {
      return {
        arrivalTime: false,
        connectionTime: false,
        logoutTime: false,
        breakTime: false,
      };
    },
    createDefaultTimeContinued() {
      return {
        arrivalTime: false,
        connectionTime: false,
        logoutTime: false,
      };
    },
    normalizeTimeContinued(value) {
      return {
        ...this.createDefaultTimeContinued(),
        ...(value || {}),
      };
    },
    normalizeTimeModified(value) {
      return {
        ...this.createDefaultTimeModified(),
        ...(value || {}),
      };
    },
    generateManualModelKey() {
      return `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    },
    getReportModelKey(model) {
      const modelId = Number(model?.model_id || model?.id || 0);
      if (modelId > 0) return `id::${modelId}`;
      if (model?.manualEntry && model?.manualKey) return `manual::${model.manualKey}`;

      const studioId = Number(model?.studio_id || 0);
      const fullName = String(model?.fullName || model?.name || '').trim().toLowerCase();
      const stageName = String(model?.stageName || '').trim().toLowerCase();
      return `${studioId}::${fullName}::${stageName}`;
    },
    getSatelliteModelKey(model) {
      return this.getReportModelKey(model);
    },
    isSatelliteModelSelected(model) {
      const targetKey = this.getSatelliteModelKey(model);
      return this.modelsList.some((item) => this.getSatelliteModelKey(item) === targetKey);
    },
    isStudioModelSelected(model) {
      const targetKey = this.getReportModelKey(model);
      return this.modelsList.some((item) => this.getReportModelKey(item) === targetKey);
    },
    getStudioChipStyle(model) {
      const selected = this.isStudioModelSelected(model);
      const selectedColor = this.headerColor && this.headerColor !== '#000000'
        ? this.headerColor
        : '#2e7d32';

      return {
        backgroundColor: selected ? selectedColor : '#ffffff',
        border: `1px solid ${selected ? selectedColor : '#1f2937'}`,
        color: selected ? '#ffffff' : '#1f2937',
      };
    },
    getStudioModelsByShift(shiftLabel) {
      return (this.studioModelOptions || [])
        .filter((model) => String(model?.sourceShift || '') === shiftLabel)
        .sort((a, b) => {
          const aLabel = String(a?.stageName || a?.fullName || a?.name || '').toLowerCase();
          const bLabel = String(b?.stageName || b?.fullName || b?.name || '').toLowerCase();
          return aLabel.localeCompare(bLabel);
        });
    },
    getSatelliteModelsByStudio(studioId) {
      return (this.satelliteModelOptions || [])
        .filter((model) => Number(model?.studio_id) === Number(studioId))
        .sort((a, b) => {
          const aLabel = String(a?.stageName || a?.fullName || a?.name || '').toLowerCase();
          const bLabel = String(b?.stageName || b?.fullName || b?.name || '').toLowerCase();
          return aLabel.localeCompare(bLabel);
        });
    },
    getSatelliteChipStyle(model) {
      const selected = this.isSatelliteModelSelected(model);
      const selectedColor = this.headerColor && this.headerColor !== '#000000'
        ? this.headerColor
        : '#2e7d32';

      return {
        backgroundColor: selected ? selectedColor : '#ffffff',
        border: `1px solid ${selected ? selectedColor : '#1f2937'}`,
        color: selected ? '#ffffff' : '#1f2937',
      };
    },
    createModelFromActiveOption(model, defaults = {}) {
      const defaultTimes = this.getDefaultTimes();
      return {
        model_id: model.id || model.model_id || null,
        manualEntry: false,
        manualKey: null,
        name: model.fullName || model.name || '',
        stageName: model.stageName || '',
        studio_id: model.studio_id || null,
        arrivalTime: { ...defaultTimes.arrival },
        connectionTime: { ...defaultTimes.connection },
        logoutTime: { ...defaultTimes.logout },
        earnings: JSON.parse(JSON.stringify(this.getEmptyEarnings())),
        assistance: defaults.assistance ?? (this.isSatelliteMonitor ? 'Yes' : ''),
        observations: '',
        room: '',
        breakTime: '',
        timeModified: this.createDefaultTimeModified(),
        timeContinued: this.createDefaultTimeContinued(),
        manualHours: '',
        isModelFocus: false,
        ...defaults,
      };
    },
    createModelFromSatelliteOption(model) {
      return this.createModelFromActiveOption(model, {
        assistance: this.isSatelliteMonitor ? 'Yes' : 'No (Offline Sales)',
      });
    },
    createManualModelEntry() {
      const defaultTimes = this.getDefaultTimes();
      return {
        model_id: null,
        manualEntry: true,
        manualKey: this.generateManualModelKey(),
        name: '',
        stageName: '',
        studio_id: this.user?.studio_id || null,
        arrivalTime: { ...defaultTimes.arrival },
        connectionTime: { ...defaultTimes.connection },
        logoutTime: { ...defaultTimes.logout },
        earnings: JSON.parse(JSON.stringify(this.getEmptyEarnings())),
        assistance: this.isSatelliteMonitor ? 'Yes' : '',
        observations: '',
        room: '',
        breakTime: '',
        timeModified: this.createDefaultTimeModified(),
        timeContinued: this.createDefaultTimeContinued(),
        manualHours: '',
        isModelFocus: false,
      };
    },
    openStudioModelDialog() {
      this.studioModelDialogTab = 'studio';
      this.studioModelDialog = true;
    },
    addManualModelRow() {
      this.modelsList.push(this.createManualModelEntry());
      this.studioModelDialog = false;
      this.ensureModelFocusSelection();
      this.unsavedChanges = true;
      this.scheduleAutosave();
    },
    toggleStudioModel(model) {
      const targetKey = this.getReportModelKey(model);
      const existingIndex = this.modelsList.findIndex(
        (item) => this.getReportModelKey(item) === targetKey
      );

      if (existingIndex >= 0) {
        this.modelsList.splice(existingIndex, 1);
      } else {
        this.modelsList.push(this.createModelFromActiveOption(model));
      }

      this.ensureModelFocusSelection();
      this.unsavedChanges = true;
      this.scheduleAutosave();
    },
    toggleSatelliteModel(model) {
      const targetKey = this.getSatelliteModelKey(model);
      const existingIndex = this.modelsList.findIndex(
        (item) => this.getSatelliteModelKey(item) === targetKey
      );

      if (existingIndex >= 0) {
        this.modelsList.splice(existingIndex, 1);
      } else {
        this.modelsList.push(this.createModelFromSatelliteOption(model));
      }

      this.sortModelsForSatellite();
      this.ensureModelFocusSelection();
      this.unsavedChanges = true;
      this.scheduleAutosave();
    },
    handleTimeFieldUpdate(model, field, value) {
      if (!model) return;
      model[field] = value;
      if (this.loadingReport) return;
      model.timeModified = this.normalizeTimeModified(model.timeModified);
      if (Object.prototype.hasOwnProperty.call(model.timeModified, field)) {
        model.timeModified[field] = true;
      }
    },
    handleTimeContinuedUpdate(model, field, value) {
      if (!model) return;
      model.timeContinued = this.normalizeTimeContinued(model.timeContinued);
      model.timeContinued[field] = value;
      if (this.loadingReport) return;
      model.timeModified = this.normalizeTimeModified(model.timeModified);
      if (Object.prototype.hasOwnProperty.call(model.timeModified, field)) {
        model.timeModified[field] = true;
      }
    },
    resetTimeTrackingRow(model) {
      if (!model) return;

      model.manualHours = '';
      const defaultTimes = this.getDefaultTimes();
      model.arrivalTime = { ...defaultTimes.arrival };
      model.connectionTime = { ...defaultTimes.connection };
      model.logoutTime = { ...defaultTimes.logout };
      model.breakTime = '';
      model.timeModified = this.createDefaultTimeModified();
      model.timeContinued = this.createDefaultTimeContinued();
    },
    getModelFocusBorderColor() {
      return this.headerColor || '#000000';
    },
    stripMfMarker(model) {
      const current = String(model?.observations || '');
      if (!current) return;

      const cleaned = current
        .replace(/(^|\n)\s*MF:\s*/gi, '$1')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

      model.observations = cleaned;
    },
    ensureMfMarker(model) {
      const current = String(model?.observations || '');
      if (/(^|\s)MF:/i.test(current)) return;

      if (!current.trim()) {
        model.observations = 'MF: ';
        return;
      }

      const separator = current.endsWith('\n') ? '\n' : '\n\n';
      model.observations = `${current}${separator}MF: `;
    },
    ensureModelFocusReasonLine() {
      const title = 'Reason for not completing Model Focus:';
      const exists = (this.observationsList || []).some((line) =>
        String(line || '').trim().toLowerCase().startsWith(title.toLowerCase())
      );
      if (exists) return;
      const firstEmptyIndex = (this.observationsList || []).findIndex(
        (line) => String(line || '').trim() === ''
      );

      if (firstEmptyIndex >= 0) {
        this.observationsList[firstEmptyIndex] = `${title} `;
        return;
      }

      this.observationsList.push(`${title} `);
    },
    clearModelFocusReasonLine() {
      const title = 'Reason for not completing Model Focus:';
      const next = (this.observationsList || []).filter((line) =>
        !String(line || '').trim().toLowerCase().startsWith(title.toLowerCase())
      );
      this.observationsList = next.length > 0 ? next : [''];
    },
    hasContentAfterMf(text) {
      const value = String(text || '');
      const idx = value.lastIndexOf('MF:');
      if (idx === -1) return false;
      return value.slice(idx + 3).trim().length > 0;
    },
    handleModelFocusObservationInput(index) {
      if (!Array.isArray(this.modelsList) || index < 0 || index >= this.modelsList.length) return;
      const model = this.modelsList[index];
      if (!model?.isModelFocus) return;

      const current = String(model.observations || '');
      if (!/(^|\n)\s*MF:/i.test(current)) {
        model.observations = `MF: ${current.trimStart()}`;
      }

      if (this.hasContentAfterMf(model.observations)) {
        this.clearModelFocusReasonLine();
      }
    },
    setModelFocus(index) {
      if (!Array.isArray(this.modelsList) || index < 0 || index >= this.modelsList.length) return;
      this.modelFocusManual = true;

      // Toggle off when clicking the currently selected model focus row
      if (this.modelsList[index]?.isModelFocus) {
        this.modelsList.forEach((m) => {
          m.isModelFocus = false;
          this.stripMfMarker(m);
        });
        this.ensureModelFocusReasonLine();
        return;
      }

      this.modelsList.forEach((m, i) => {
        m.isModelFocus = i === index;
        if (i !== index) this.stripMfMarker(m);
      });

      this.ensureMfMarker(this.modelsList[index]);
    },
    async fetchActiveModels(shift, studioId = null) {
      try {
        const params = studioId ? { shift, studioId } : { shift };
        const response = await secureAxios.get('/api/reports/models', {
          params,
        });
        return response.data;
      } catch (error) {
        console.error('❌ Error fetching active models:', error?.message || error);
        toast.error('Failed to fetch active models');
        return [];
      }
    },
    async fetchStudioModelOptions() {
      if (this.isSatelliteMonitor) return [];

      const studioId = this.user?.studio_id;
      if (!studioId) return [];

      const shifts = ['Morning', 'Evening', 'Night'];
      const results = await Promise.all(
        shifts.map(async (shiftLabel) => {
          const models = await this.fetchActiveModels(shiftLabel, studioId);
          return models.map((model) => ({
            ...model,
            sourceShift: shiftLabel,
          }));
        })
      );

      return results.flat();
    },
    async fetchMonitorConfig() {
      try {
        const studioId = this.user?.studio_id;
        if (!studioId) return;

        const response = await secureAxios.get('/api/config/monitors', {
          params: { studio_id: studioId },
        });

        const data = response.data || {};

        this.hasActiveNightMonitor = Array.isArray(data.monitors)
          ? data.monitors.some((monitor) => (
            String(monitor?.shift || '').toLowerCase() === 'night'
            && String(monitor?.status || 'Active').toLowerCase() === 'active'
          ))
          : Boolean(data.night);

        this.monitorConfig = {
          morning: data.morning || '',
          evening: data.evening || '',
          night: data.night || '',
        };

        // If monitor not manually changed and not yet set, default to current shift
        if (!this.monitorManuallyChanged && !this.selectedMonitor) {
          this.selectedMonitor = this.monitorConfig?.[this.activeTab] || '';
        }

      } catch (error) {
        console.error('❌ Error fetching monitor config:', error?.message || error);
      }
    },
    applyInitialReportContext(now = new Date()) {
      if (this.isSatelliteMonitor) return;

      const bogota = bogotaDateTimeParts(now);
      let activeTab = bogota.hour >= 6 && bogota.hour < 14 ? 'morning' : 'evening';
      let reportDate = localReportDate(bogota);

      if (this.hasActiveNightMonitor && (bogota.hour >= 22 || bogota.hour < 6)) {
        activeTab = 'night';
        // After midnight, keep working in the report dated when Night began.
        if (bogota.hour < 6) reportDate = localReportDate(bogota, -1);
      }

      this.activeTab = activeTab;
      this.reportDate = reportDate;
      this.selectedMonitor = this.monitorConfig?.[activeTab] || '';
    },
    async fetchOperationalTasks(shiftOverride = null) {
      const shift = shiftOverride || (this.activeTab === 'satellite'
        ? 'Satellite'
        : this.activeTab.charAt(0).toUpperCase() + this.activeTab.slice(1));
      try {
        const { data } = await secureAxios.get('/api/operational-tasks', { params: { shift } });
        this.operationalTasks = Array.isArray(data) ? data : [];
      } catch (error) {
        console.warn('Operational tasks unavailable in daily report:', error?.message || error);
        this.operationalTasks = [];
      }
    },
    fetchReportData: debounce(async function () {
      const date = this.formatDate(this.reportDate);
      const shift = this.activeTab === 'satellite'
        ? 'Satellite'
        : this.activeTab.charAt(0).toUpperCase() + this.activeTab.slice(1);
      const requestKey = `${date}|${shift.toLowerCase()}|${Number(this.user?.studio_id) || 0}`;
      const requestId = ++this.reportFetchSequence;
      this.loadingReport = true;
      this.loadedReportKey = null;

      try {
        const params = this.isSatelliteMonitor
          ? { date, shift }
          : { date, shift, studio_id: this.user?.studio_id };
        const response = await secureAxios.get('/api/reports', {
          params,
        });
        if (requestId !== this.reportFetchSequence || requestKey !== this.getCurrentReportKey()) return;

        const reportData = response.data;
        let scheduledFocusIds = [];
        try {
          const { data } = await secureAxios.get('/api/reports/schedule-focus', { params: { date, shift } });
          scheduledFocusIds = data.modelIds || [];
        } catch (error) { console.warn('Scheduled Model Focus unavailable; keeping report selection.', error?.message); }
        if (requestId !== this.reportFetchSequence || requestKey !== this.getCurrentReportKey()) return;
        this.scheduledFocusIds = scheduledFocusIds;
        // Legacy saved selections may be manual: preserve them rather than rewrite history.
        this.modelFocusManual = reportData?.modelFocusManual ?? !!reportData?.modelsList?.some(model => model.isModelFocus);
        this.reportVersion = reportData?._reportVersion || null;
        let activeModels = [];

        if (this.isSatelliteMonitor) {
          activeModels = await this.fetchActiveModels(shift);
          this.satelliteModelOptions = activeModels;
        } else {
          const [currentShiftModels, studioShiftOptions, studioSatelliteOptions] = await Promise.all([
            this.fetchActiveModels(shift, this.user?.studio_id),
            this.fetchStudioModelOptions(),
            this.fetchActiveModels('Satellite', this.user?.studio_id),
          ]);
          activeModels = currentShiftModels;
          this.studioModelOptions = studioShiftOptions;
          this.satelliteModelOptions = studioSatelliteOptions;
        }


        if (reportData) {
          Object.keys(reportData).forEach((prop) => {
            if (prop === '_reportVersion') return;
            if (this.$data.hasOwnProperty(prop)) {
              if (prop === 'reportDate') {
                this[prop] = new Date(reportData[prop]);
              } else if (prop === 'modelsList') {
                this[prop] = this.normalizeModelFocusState(reportData[prop].map((model) => ({
                  ...model,
                  model_id: model.model_id || model.id || null,
                  manualEntry: !!model.manualEntry,
                  manualKey: model.manualKey || (model.manualEntry ? this.generateManualModelKey() : null),
                  arrivalTime: model.arrivalTime || this.getDefaultTimes().arrival,
                  connectionTime: model.connectionTime || this.getDefaultTimes().connection,
                  logoutTime: model.logoutTime || this.getDefaultTimes().logout,
                  // Deep clone earnings to avoid shared references between models
                  earnings: this.normalizeEarnings(model.earnings),
                  assistance: model.assistance || '',
                  observations: model.observations || '',
                  stageName: model.stageName || '',
                  room: model.room || '',
                  breakTime: model.breakTime || '',
                  timeModified: this.normalizeTimeModified(model.timeModified),
                  timeContinued: this.normalizeTimeContinued(model.timeContinued),
                  manualHours: model.manualHours !== undefined ? model.manualHours : '',
                  isModelFocus: !!model.isModelFocus,
                })));
                this.sortModelsForSatellite();
                this.ensureModelFocusSelection();
              } else if (prop === 'adjustmentList') {
                const rows = Array.isArray(reportData[prop]) ? reportData[prop] : [];
                this[prop] = rows.length > 0
                  ? rows.map((entry) => this.normalizeReportAdjustment(entry))
                  : [createEmptyAdjustment()];
              } else {
                this[prop] = reportData[prop];
              }
            }
          });
          // Set default monitor from reportData.monitor if present
          if (reportData && reportData.monitor) {
            this.selectedMonitor = reportData.monitor;
          }
        } else {
          this.selectedMonitor = this.isSatelliteMonitor
            ? 'Diana Bolaños'
            : this.monitorConfig?.[this.activeTab] || '';
          this.modelsList = this.isSatelliteMonitor
            ? []
            : activeModels.length > 0
              ? activeModels.map((model) => this.createModelFromActiveOption(model))
              : [this.createManualModelEntry()];
          this.sortModelsForSatellite();
          this.ensureModelFocusSelection();
          this.breakEntry = '';
          this.usageEntries = {
            lush1Entry: 'Monitors Desk',
            lush2Entry: 'Monitors Desk',
            sexMachineEntry: 'Monitors Desk',
            cellphoneEntry: 'Monitors Desk',
          };
          this.adjustmentList = [
            createEmptyAdjustment(),
          ];
          this.observationsList = [''];
          this.checklistItems = this.checklistItems.map((item) => ({ ...item, checked: false }));
          this.headerColor = '#000000';
        }
        await Promise.all([this.fetchInventoryItems(), this.fetchOperationalTasks(shift)]);
        if (requestId !== this.reportFetchSequence || requestKey !== this.getCurrentReportKey()) return;
        this.unsavedChanges = false; // Reset after successful fetch
        this.loadedReportKey = requestKey;
        this.loadingReport = false;
      } catch (error) {
        if (requestId !== this.reportFetchSequence) return;
        console.error('❌ Error fetching report:', error?.message || error);
        toast.error('Failed to fetch report data');
        this.loadedReportKey = null;
        this.reportVersion = null;
        this.unsavedChanges = false;
        this.loadingReport = false;
      }
    }, 300),
    scheduleAutosave: debounce(function () {
      if (this.isSupportAccount) return;
      if (this.unsavedChanges && !this.loadingReport && this.loadedReportKey === this.getCurrentReportKey()) {
        this.saveReport(true);
      }
    }, 2000), // 2-second debounce for responsiveness
    async saveReport(autosave = false) {
      if (this.isSupportAccount) return;
      const date = this.formatDate(this.reportDate);
      const shift = this.activeTab === 'satellite' ? 'Satellite' : this.activeTab;
      const saveKey = this.getCurrentReportKey();
      if (this.loadingReport || this.loadedReportKey !== saveKey) return;
      const reportData = {
        activeTab: this.activeTab,
        selectedMonitor: this.selectedMonitor,
        reportDate: this.reportDate.toISOString(),
        headerColor: this.headerColor,
        observationsList: this.observationsList,
        modelsList: this.modelsList,
        modelFocusManual: this.modelFocusManual,
        satelliteShiftStartTime: this.satelliteShiftStartTime,
        satelliteShiftEndTime: this.satelliteShiftEndTime,
        hasSecondSatelliteShift: this.hasSecondSatelliteShift,
        satelliteSecondShiftStartTime: this.satelliteSecondShiftStartTime,
        satelliteSecondShiftEndTime: this.satelliteSecondShiftEndTime,
        breakEntry: this.breakEntry,
        usageEntries: this.usageEntries,
        adjustmentList: this.prepareAdjustmentRowsForSave(),
        checklistItems: this.checklistItems,
      };
      try {
        const payload = { date, shift, reportData, expectedVersion: this.reportVersion };
        const response = await secureAxios.post('/api/reports/save', payload);
        if (saveKey !== this.getCurrentReportKey()) return;
        this.reportVersion = response.data?.reportVersion || this.reportVersion;
        if (!autosave) {
          toast.success(`Report saved for ${date} ${shift}`);
        }
        this.unsavedChanges = false;
      } catch (error) {
        console.error(`❌ Error ${autosave ? 'autosaving' : 'saving'} report:`, error?.message || error);
        if (error?.response?.status === 409) {
          toast.error(error.response.data?.error || 'This report changed elsewhere. Reload before saving.');
        } else {
          toast.error(`Failed to ${autosave ? 'autosave' : 'save'} report`);
        }
      }
    },
    getCurrentReportKey() {
      const date = this.formatDate(this.reportDate);
      const shift = this.activeTab === 'satellite'
        ? 'satellite'
        : String(this.activeTab || '').toLowerCase();
      return `${date}|${shift}|${Number(this.user?.studio_id) || 0}`;
    },
    getDefaultTimes() {
      // Satellite monitors should never inherit Night shift defaults.
      // Use a friendly daytime block to reduce AM/PM switching.
      if (this.isSatelliteMonitor) {
        return {
          arrival: { hours: 7, minutes: 0, ampm: 'AM' },
          connection: { ...this.satelliteShiftStartTime },
          logout: { ...this.satelliteShiftEndTime },
        };
      }

      return {
        morning: {
          arrival: { hours: 5, minutes: 30, ampm: 'AM' },
          connection: { hours: 6, minutes: 0, ampm: 'AM' },
          logout: { hours: 1, minutes: 40, ampm: 'PM' },
        },
        evening: {
          arrival: { hours: 1, minutes: 30, ampm: 'PM' },
          connection: { hours: 2, minutes: 0, ampm: 'PM' },
          logout: { hours: 9, minutes: 40, ampm: 'PM' },
        },
        night: {
          arrival: { hours: 9, minutes: 30, ampm: 'PM' },
          connection: { hours: 10, minutes: 0, ampm: 'PM' },
          logout: { hours: 5, minutes: 40, ampm: 'AM' },
        },
      }[this.activeTab] || {
        arrival: { hours: 9, minutes: 30, ampm: 'PM' },
        connection: { hours: 10, minutes: 0, ampm: 'PM' },
        logout: { hours: 5, minutes: 40, ampm: 'AM' },
      };
    },
    addNewModel() {
      this.modelsList.push(this.createManualModelEntry());
      this.sortModelsForSatellite();
      this.ensureModelFocusSelection();
    },
    removeModel(index) {
      this.modelsList.splice(index, 1);
      this.ensureModelFocusSelection();
    },
    toggleCurrency(entry) {
      const currencies = ['EUR', 'COP', 'USD'];
      const currentIndex = currencies.indexOf(entry.currency);
      entry.currency = currencies[(currentIndex + 1) % currencies.length];
    },
    getEarningsClass(amount) {
      if (amount <= 29.99) return 'red-bg';
      else if (amount >= 30 && amount < 60) return 'yellow-bg';
      else if (amount >= 60) return 'green-bg';
      return '';
    },
    getGrandTotalEarningsClass() {
      const relevantModels = this.modelsList.filter((model) => model.assistance === 'Yes');
      const numModels = relevantModels.length;
      if (numModels === 0) return '';

      const redThreshold = 29.99 * numModels;
      const yellowThreshold = 59.99 * numModels;
      const adjustedTotal = this.grandTotal;

      if (adjustedTotal <= redThreshold) return 'red-bg';
      else if (adjustedTotal > redThreshold && adjustedTotal <= yellowThreshold) return 'yellow-bg';
      else if (adjustedTotal > yellowThreshold) return 'green-bg';
      return '';
    },
    parseBreakMinutes(breakValue) {
      const raw = String(breakValue || '').trim();
      if (!raw) return 0;

      const [hoursRaw, minutesRaw] = raw.split(':');
      const hours = Number(hoursRaw || 0);
      const minutes = Number(minutesRaw || 0);

      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
      return Math.max(0, (hours * 60) + minutes);
    },
    calculateOnlineHours(model) {
      if (!model.connectionTime || !model.logoutTime) return '0:00';
      const convertTo24hr = (time) => {
        let hours = time.hours;
        if (time.ampm === 'PM' && time.hours !== 12) hours += 12;
        if (time.ampm === 'AM' && time.hours === 12) hours = 0;
        return { hours, minutes: time.minutes };
      };
      const connection = convertTo24hr(model.connectionTime);
      const logout = convertTo24hr(model.logoutTime);
      let connectionDate = new Date(0, 0, 0, connection.hours, connection.minutes);
      let logoutDate = new Date(0, 0, 0, logout.hours, logout.minutes);
      if (logoutDate < connectionDate) logoutDate.setDate(logoutDate.getDate() + 1);
      let diffMs = logoutDate - connectionDate;
      let totalMinutes = Math.floor(diffMs / (1000 * 60));
      totalMinutes -= this.parseBreakMinutes(model.breakTime);
      totalMinutes = Math.max(0, totalMinutes);

      const diffHours = Math.floor(totalMinutes / 60);
      const diffMinutes = totalMinutes % 60;
      return `${diffHours} : ${diffMinutes.toString().padStart(2, '0')}`;
    },
    toggleSatelliteSplitShift() {
      const nextState = !this.hasSecondSatelliteShift;
      this.hasSecondSatelliteShift = nextState;

      if (nextState) {
        this.satelliteSecondShiftStartTime = { ...this.satelliteShiftStartTime };
        this.satelliteSecondShiftEndTime = { ...this.satelliteShiftEndTime };
      }
    },
    changeDate(days) {
      let currentDate = new Date(this.reportDate);
      currentDate.setDate(currentDate.getDate() + days);
      this.reportDate = currentDate;
    },
    updateObservation(model) {
      if (!model?.observations || !String(model.observations).includes(' - ')) return;
      const [, ...rest] = String(model.observations).split(' - ');
      model.observations = `${model.name || ''} - ${rest.join(' - ')}`;
    },
    initializeObservation(model) {
      if (!model.observations || model.observations.trim() === '') {
        model.observations = `${model.name} - `;
      } else if (!model.observations.startsWith(`${model.name} - `)) {
        model.observations = `${model.name} - ${model.observations}`;
      }
    },
    
    addObservationRow() {
      this.observationsList.push('');
      this.$nextTick(() => {
        const index = this.observationsList.length - 1;
        const ref = this.$refs[`observation-${index}`];
        if (ref && ref[0] && typeof ref[0].focus === 'function') {
          ref[0].focus();
        }
      });
    },
    removeObservationRow(index) {
      if (this.observationsList.length > 1) this.observationsList.splice(index, 1);
    },
    getEmptyEarnings() {
      return JSON.parse(JSON.stringify(
        this.platforms.reduce((acc, platform) => {
          acc[platform] = 0;
          return acc;
        }, {})
      ));
    },
    formatDate(date) {
      const d = date instanceof Date ? date : new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatCurrency(value) {
      if (isNaN(value) || value === '') return '$0.00';
      return value < 0 ? `-${Math.abs(value).toFixed(2)}` : `$${parseFloat(value).toFixed(2)}`;
    },
    updateEarnings(index, platform, event) {
      let rawValue = event.target.value;
      rawValue = rawValue.replace(/[^0-9.-]/g, '');
      if (rawValue.indexOf('-') !== -1) {
        const minusIndex = rawValue.indexOf('-');
        rawValue =
          '-' + rawValue.slice(0, minusIndex).replace('-', '') + rawValue.slice(minusIndex + 1).replace(/-/g, '');
      }
      if (rawValue === '-' || rawValue === '') {
        this.modelsList[index].earnings[platform] = rawValue;
        return;
      }
      const decimalCount = (rawValue.match(/\./g) || []).length;
      if (decimalCount > 1) {
        const firstDecimalIndex = rawValue.indexOf('.');
        rawValue = rawValue.slice(0, firstDecimalIndex + 1) + rawValue.slice(firstDecimalIndex + 1).replace(/\./g, '');
      }
      if (rawValue.match(/^(-?)0+([1-9])/)) {
        rawValue = rawValue.replace(/^(-?)0+([1-9])/, '$1$2');
      }
      this.modelsList[index].earnings[platform] = rawValue;
    },
    formatEarnings(index, platform) {
      let value = this.modelsList[index].earnings[platform];
      if (value !== '' && !isNaN(value)) {
        this.modelsList[index].earnings[platform] = parseFloat(value).toFixed(2);
      }
    },
    getRowTotal(earnings) {
      return this.platforms.reduce(
        (sum, platform) => sum + (Number(earnings?.[platform]) || 0),
        0
      );
    },
    addAdjustmentRow() {
      this.adjustmentList.push(createEmptyAdjustment());
    },
    removeAdjustmentRow(index) {
      this.adjustmentList.splice(index, 1);
    },
    normalizeReportAdjustment(entry = {}) {
      const isBlankRow = !String(entry.name || '').trim()
        && !String(entry.description || '').trim()
        && Number(entry.amount || 0) === 0;
      return {
        ...createEmptyAdjustment(),
        ...entry,
        source_id: entry.source_id || createAdjustmentSourceId(),
        model_id: Number(entry.model_id) || null,
        model_studio_id: Number(entry.model_studio_id || entry.studio_id) || null,
        inventory_item_id: Number(entry.inventory_item_id) || null,
        inventory_unit_price: Number(entry.inventory_unit_price) || null,
        inventory_menu_open: false,
        type: isBlankRow ? 'Purchase' : (entry.type || 'Purchase'),
        quantity: Math.max(1, Number.parseInt(entry.quantity ?? entry.inventory_quantity, 10) || 1),
        quotes: String(entry.quotes || entry.quotas || '1'),
      };
    },
    async fetchInventoryItems() {
      try {
        const studioIds = this.isSatelliteMonitor
          ? [...new Set(this.modelsList.map((model) => Number(model.studio_id)).filter((id) => id > 0))]
          : [Number(this.user?.studio_id)].filter((id) => id > 0);
        const responses = await Promise.all(
          studioIds.map((studioId) => secureAxios.get('/api/inventory/items', {
            params: this.isSatelliteMonitor ? { studio_id: studioId } : undefined,
          }))
        );
        this.inventoryItems = responses.flatMap((response, index) => (
          (response.data || []).map((item) => ({ ...item, studio_id: studioIds[index] }))
        ));
        this.adjustmentList.forEach((entry) => {
          const selectedItem = this.inventoryItems.find((item) => (
            Number(item.id) === Number(entry.inventory_item_id)
            && (!entry.model_studio_id || Number(item.studio_id) === Number(entry.model_studio_id))
          ));
          if (selectedItem) entry.inventory_unit_price = Number(selectedItem.default_selling_price || 0);
        });
      } catch (error) {
        console.error('Error loading report inventory choices:', error?.message || error);
        this.inventoryItems = [];
      }
    },
    inventoryOptionsForAdjustment(entry) {
      const wantedName = String(entry.name || '').trim().toLowerCase();
      const matchingModels = this.modelsList.filter((model) => {
        const fullName = String(model.name || model.fullName || '').trim().toLowerCase();
        const stageName = String(model.stageName || '').trim().toLowerCase();
        return wantedName && (wantedName === fullName || wantedName === stageName);
      });
      const modelStudioId = matchingModels.length === 1
        ? Number(matchingModels[0].studio_id)
        : Number(entry.model_studio_id);
      const choices = modelStudioId
        ? this.inventoryItems.filter((item) => Number(item.studio_id) === modelStudioId)
        : this.inventoryItems;
      return choices
        .filter((item) => Number(item.quantity_on_hand || 0) >= 1)
        .filter((item, index, list) => (
          list.findIndex((candidate) => Number(candidate.id) === Number(item.id)) === index
        ));
    },
    handleAdjustmentDescription(entry, value) {
      const description = typeof value === 'object' ? value?.name : value;
      entry.description = String(description || '');
      const normalized = entry.description.trim().toLowerCase();
      const matches = this.inventoryOptionsForAdjustment(entry).filter(
        (item) => String(item.name || '').trim().toLowerCase() === normalized
      );
      entry.inventory_item_id = matches.length === 1 ? Number(matches[0].id) : null;
      entry.inventory_unit_price = matches.length === 1
        ? Number(matches[0].default_selling_price || 0)
        : null;
      if (matches.length === 1 && Number(entry.amount || 0) <= 0) {
        entry.amount = Number(matches[0].default_selling_price || 0) * Math.max(1, Number(entry.quantity || 1));
        entry.currency = 'COP';
      }
      entry.inventory_menu_open = !!entry.description;
    },
    handleAdjustmentModelChange(entry, value) {
      const wantedName = String(typeof value === 'object' ? (value?.name || value?.title || '') : value || '')
        .trim()
        .toLowerCase();
      entry.name = String(typeof value === 'object' ? (value?.name || value?.title || '') : value || '');
      const studioAccount = this.studioAccountOptions.find(
        studio => studio.name.toLowerCase() === wantedName
      );
      if (studioAccount) {
        entry.model_id = null;
        entry.model_studio_id = studioAccount.id;
        return;
      }
      const matches = this.modelsList.filter((model) => {
        const fullName = String(model.name || model.fullName || '').trim().toLowerCase();
        const stageName = String(model.stageName || '').trim().toLowerCase();
        return wantedName && (wantedName === fullName || wantedName === stageName);
      });
      const matchedModel = matches.length === 1 ? matches[0] : null;
      entry.model_id = Number(matchedModel?.model_id || matchedModel?.id) || null;
      entry.model_studio_id = Number(matchedModel?.studio_id) || null;

      if (entry.inventory_item_id && entry.model_studio_id) {
        const selectedItemIsAvailable = this.inventoryItems.some((item) => (
          Number(item.id) === Number(entry.inventory_item_id)
          && Number(item.studio_id) === Number(entry.model_studio_id)
        ));
        if (!selectedItemIsAvailable) {
          entry.inventory_item_id = null;
          entry.inventory_unit_price = null;
          entry.description = '';
          entry.amount = 0;
        }
      }
    },
    filteredInventoryOptionsForAdjustment(entry) {
      const search = String(entry.description || '').trim().toLowerCase();
      const choices = this.inventoryOptionsForAdjustment(entry);
      if (!search) return choices.slice(0, 12);
      return choices
        .filter((item) => String(item.name || '').toLowerCase().includes(search))
        .slice(0, 12);
    },
    selectInventoryAdjustmentItem(entry, item) {
      entry.description = item.name;
      entry.inventory_item_id = Number(item.id);
      entry.inventory_unit_price = Number(item.default_selling_price || 0);
      entry.amount = entry.inventory_unit_price * Math.max(1, Number(entry.quantity || 1));
      entry.currency = 'COP';
      entry.inventory_menu_open = false;
    },
    handleAdjustmentQuantityChange(entry, value) {
      const quantity = Math.max(1, Number.parseInt(value, 10) || 1);
      entry.quantity = quantity;
      const unitPrice = Number(entry.inventory_unit_price || 0);
      if (entry.type === 'Purchase' && entry.inventory_item_id && unitPrice > 0) {
        entry.amount = unitPrice * quantity;
        entry.currency = 'COP';
      }
    },
    handleAdjustmentTypeChange(entry) {
      entry.inventory_menu_open = false;
      if (entry.type !== 'Purchase') {
        entry.inventory_item_id = null;
        entry.inventory_unit_price = null;
      }
    },
    prepareAdjustmentRowsForSave() {
      return this.adjustmentList.map((entry, index) => {
        if (!entry.source_id) this.adjustmentList[index].source_id = createAdjustmentSourceId();
        const wantedName = String(entry.name || '').trim().toLowerCase();
        const studioAccount = this.studioAccountOptions.find(
          studio => studio.name.toLowerCase() === wantedName
        );
        const matches = this.modelsList.filter((model) => {
          const fullName = String(model.name || model.fullName || '').trim().toLowerCase();
          const stageName = String(model.stageName || '').trim().toLowerCase();
          return wantedName && (wantedName === fullName || wantedName === stageName);
        });
        const matchedModel = matches.length === 1 ? matches[0] : null;
        const isStudioAccount = Boolean(studioAccount);
        return {
          ...entry,
          source_id: this.adjustmentList[index].source_id,
          model_id: isStudioAccount ? null : (Number(matchedModel?.model_id || matchedModel?.id) || null),
          model_studio_id: isStudioAccount
            ? studioAccount.id
            : (Number(matchedModel?.studio_id) || null),
          inventory_item_id: Number(entry.inventory_item_id) || null,
          quantity: Math.max(1, Number.parseInt(entry.quantity, 10) || 1),
          inventory_menu_open: undefined,
        };
      });
    },
    sendReport() {
      const reportData = {
        activeTab: this.activeTab,
        selectedMonitor: this.selectedMonitor,
        reportDate: this.formatDate(this.reportDate),
        modelsList: this.modelsList,
        breakEntry: this.breakEntry,
        usageEntries: this.usageEntries,
        adjustmentList: this.adjustmentList,
        observationsList: this.observationsList,
        checklistItems: this.checklistItems,
        grandTotal: this.grandTotal,
        platformTotals: this.platformTotals,
      };
      toast.info('Send report functionality not yet implemented');
    },
    getFormattedReportData() {
    const formatTime = (timeObj) => {
      if (!timeObj || !('hours' in timeObj) || !('minutes' in timeObj) || !('ampm' in timeObj)) return '-';
      return `${timeObj.hours}:${timeObj.minutes.toString().padStart(2, '0')} ${timeObj.ampm}`;
    };

    // Only include models whose assistance is marked as "Yes" for time tracking
    const allModelsWithTime = this.modelsList
      .filter(model => model.assistance && model.assistance.toLowerCase() === 'yes')
      .map(model => ({
        fullName: model.name,
        stageName: model.stageName || '-',
        arrival: formatTime(model.arrivalTime),
        connection: formatTime(model.connectionTime),
        logout: formatTime(model.logoutTime),
        breakTime: model.breakTime || '-',
        timeModified: model.timeModified || {},
        timeContinued: model.timeContinued || {},
        hours: model.manualHours || this.calculateOnlineHours(model)
      }));

    // Sales data should include models with assistance "Yes" or "No (Offline Sales)"
    // Deep clone to avoid reference bleed
    const earningsModelsCloned = JSON.parse(JSON.stringify(this.earningsModels));
    const salesData = earningsModelsCloned.map(model => {
      const timeData = allModelsWithTime.find(m => m.fullName === model.name) || {};
      return {
        fullName: model.name,
        earnings: this.normalizeEarnings(model.earnings),
        total: this.getRowTotal(model.earnings),
        stageName: model.stageName || '-',
        arrival: timeData.arrival || '-',
        connection: timeData.connection || '-',
        logout: timeData.logout || '-',
        breakTime: timeData.breakTime || '-',
        timeModified: timeData.timeModified || model.timeModified || {},
        timeContinued: timeData.timeContinued || model.timeContinued || {},
        hours: model.manualHours || timeData.hours || '-',
        goalColor: this.getEarningsClass(this.getRowTotal(model.earnings))
      };
    });

      return {
      shift: this.activeTab,
      date: this.formatDate(this.reportDate),
      monitor: this.selectedMonitor,
      headerColor: this.headerColor,
      satelliteShiftStart: this.isSatelliteMonitor ? formatTime(this.satelliteShiftStartTime) : '',
      satelliteShiftEnd: this.isSatelliteMonitor ? formatTime(this.satelliteShiftEndTime) : '',
      hasSecondSatelliteShift: this.isSatelliteMonitor ? this.hasSecondSatelliteShift : false,
      satelliteSecondShiftStart: this.isSatelliteMonitor && this.hasSecondSatelliteShift
        ? formatTime(this.satelliteSecondShiftStartTime)
        : '',
      satelliteSecondShiftEnd: this.isSatelliteMonitor && this.hasSecondSatelliteShift
        ? formatTime(this.satelliteSecondShiftEndTime)
        : '',
      attendance: this.modelsList.map(model => ({
        fullName: model.name,
        stageName: model.stageName || '-',
        room: model.room || '-',
        assistance: model.assistance || '-'
      })),
      salesData: salesData,
      reportSites: this.reportSites.map(site => ({
        ...site,
        shortLabel: REPORT_SITE_SHORT_LABELS[site.displayName] || site.displayName,
      })),
      modelFocus: this.modelFocusModels.map(model => ({
        focus: model.name,
        notes: model.observations || '-',
        stageName: model.stageName || '-',
        studio_id: model.studio_id || null,
        room: model.room || '-',
        isModelFocus: !!model.isModelFocus
      })),
      breakItems: [{
        break: this.breakEntry || '-',
        lush1: this.usageEntries.lush1Entry || '-',
        lush2: this.usageEntries.lush2Entry || '-',
        machine: this.usageEntries.sexMachineEntry || '-',
        cellphone: this.usageEntries.cellphoneEntry || '-'
      }],
      adjustmentList: this.prepareAdjustmentRowsForSave().map(adj => ({
        source_id: adj.source_id,
        model_id: adj.model_id,
        model_studio_id: adj.model_studio_id,
        inventory_item_id: adj.inventory_item_id,
        name: adj.name || '-',
        type: adj.type || '-',
        amount: adj.amount || 0,
        currency: adj.currency || 'COP',
        description: adj.description || '-',
        quantity: adj.quantity || 1,
        quotas: adj.quotes || '-'
      })),
      shiftObservations: this.observationsList.filter(obs => obs.trim() !== ''),
      operationalTasks: this.operationalTasks,
      checklist: this.checklistItems.map(item => `${item.checked ? '✅ ' : '❌ '} ${item.text}`),
      platformTotals: this.platformTotals,
      grandTotal: this.grandTotal,
      studio_id: this.user?.studio_id || 1,
    };
  },
    handleBeforeUnload(event) {
      if (this.unsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    onCopySuccess() {
      toast.success('Report copied to clipboard!');
    },
    onCopyFailed(err) {
      console.error('Copy failed:', err?.message || err);
      toast.error('Failed to copy report');
    },
    confirmCopyReport() {
      this.showTimeTrackingWarning = false;
      this.$refs.clipboard?.copyToClipboard?.();
      toast.success('Report copied to clipboard!');
    },
    hasRoomConflict(model) {
      if (model.assistance !== 'Yes' || !model.room) return false;
      const modelsInSameRoom = this.modelsList.filter(
        (m) => m !== model && m.assistance === 'Yes' && m.room === model.room
      );
      return modelsInSameRoom.length > 0;
    },
    handleCopyClick() {
      if (!this.hasIncompleteTimeTracking) {
        this.$refs.clipboard?.copyToClipboard?.();
        toast.success('Report copied to clipboard!');
        return;
      }
      this.showTimeTrackingWarning = true;
    },
    handleEmptyEarnings(model, platform) {
      if (model.earnings[platform] === '') {
        model.earnings[platform] = 0;
      }
    },
  },
  async mounted() {
    await this.fetchActiveReportSites();
    if (this.isSatelliteMonitor) {
      this.activeTab = 'satellite';
      this.selectedMonitor = 'Diana Bolaños';
      await this.fetchMonitorConfig();
    } else {
      await this.fetchMonitorConfig();
      this.applyInitialReportContext();
    }
    this.initializingReportContext = false;
    if (!this.fetchedOnce) {
      this.fetchReportData();
      this.fetchedOnce = true;
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.unsavedChanges && this.loadedReportKey === this.getCurrentReportKey()) {
      this.saveReport(true);
    }
  },
  
  
};
</script>

<style scoped>
.dashboard-tab .v-tab {
  background-color: white;
  color: #2C74B3;
}

.earnings-grid {
  display: grid;
  align-items: center;
  column-gap: 6px;
  width: 100%;
}

.earnings-grid > * {
  min-width: 0;
}

.earnings-grid-header {
  min-height: 40px;
  height: auto !important;
}

.earnings-grid-heading {
  min-width: 0;
  text-align: center;
  white-space: nowrap;
}

.earnings-grid-row {
  margin-bottom: 4px;
}

.earnings-grid-total {
  min-height: 38px;
}

.shift-title {
  font-size: 30px;
  font-weight: bold;
  color: black;
  text-align: center;
}

.total-earnings {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 5px;
  color: rgb(0, 0, 0);
  text-align: center;
  min-width: 70px;
}

.red-bg {
  background-color: #e74d3c3c;
}

.yellow-bg {
  background-color: #f1c40f58;
  color: black;
}

.green-bg {
  background-color: #2ecc7051;
}

:deep(.mf-focus-field.is-mf .v-field__outline) {
  --v-field-border-width: 3px !important;
  color: var(--mf-border-color, #000000) !important;
}

.time-tracking-review {
  margin-bottom: 12px;
}

.time-tracking-review-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.time-tracking-review-title,
.time-tracking-review-count {
  font-size: 0.92rem;
  color: rgba(0, 0, 0, 0.68);
}

.time-tracking-review-title {
  font-weight: 700;
}

.time-tracking-review-count {
  font-weight: 600;
}

.time-tracking-review-title.is-complete,
.time-tracking-review-count.is-complete {
  color: rgba(0, 0, 0, 0.82);
}

.time-tracking-progress-track {
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.time-tracking-progress-fill {
  height: 100%;
  border-radius: 999px;
  opacity: 0.78;
  transition: width 180ms ease;
}

.time-tracking-warning-card {
  border-radius: 12px;
}

.time-tracking-warning-box {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
  padding: 14px 16px;
}

.time-tracking-warning-count {
  font-size: 0.98rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.82);
  margin-bottom: 8px;
}

.time-tracking-warning-copy {
  color: rgba(0, 0, 0, 0.68);
  margin-bottom: 8px;
  line-height: 1.45;
}

.time-tracking-warning-muted {
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.9rem;
}

.time-tracking-compact-field :deep(.v-field__input) {
  justify-content: center;
  text-align: center;
  padding-inline: 8px !important;
}

.time-tracking-compact-field :deep(input) {
  text-align: center;
  font-weight: 700;
}

.time-tracking-compact-field :deep(input::placeholder) {
  color: rgba(0, 0, 0, 0.88);
  opacity: 1;
  font-weight: 700;
}

.studio-model-dialog,
.satellite-model-dialog {
  border-radius: 8px;
}

.studio-selector-body,
.satellite-selector-body {
  padding-left: 12px !important;
  padding-right: 12px !important;
}

.studio-selector-header,
.satellite-selector-header {
  min-height: 40px;
}

.studio-selector-manual-btn {
  font-weight: 700;
  letter-spacing: 0.02em;
}

.studio-selector-copy {
  font-size: 0.92rem;
  color: rgba(0, 0, 0, 0.66);
  margin-bottom: 12px;
}

.studio-selector-tabs {
  margin-bottom: 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.studio-selector-empty {
  color: rgba(0, 0, 0, 0.52);
  font-size: 0.9rem;
}

.studio-shift-section + .studio-shift-section {
  margin-top: 14px;
}

.studio-shift-title {
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.64);
  margin-bottom: 8px;
}

.studio-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-content: flex-start;
  min-height: 36px;
}

.satellite-shift-strip {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  padding: 0 4px;
  flex-wrap: nowrap;
  width: 100%;
}

.satellite-shift-strip-title {
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.62);
  min-width: 52px;
  flex-shrink: 0;
}

.satellite-shift-strip-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.satellite-shift-strip-group-secondary {
  margin-left: auto;
}

.satellite-shift-strip-field {
  width: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.satellite-shift-strip-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  opacity: 0.9;
  white-space: nowrap;
}

.satellite-shift-strip-toggle {
  margin-left: 4px;
  color: rgba(0, 0, 0, 0.6);
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 999px;
}

.satellite-shift-strip-toggle:hover {
  color: rgba(0, 0, 0, 0.82);
}

.satellite-shift-strip :deep(.time-picker-shell) {
  width: 170px;
}

.satellite-column-title {
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.68);
  margin-bottom: 10px;
}

.satellite-subgroup-title {
  margin-top: 6px;
}

.satellite-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-content: flex-start;
  min-height: 36px;
}

.studio-model-chip,
.satellite-model-chip {
  font-weight: 600;
  border-radius: 6px !important;
  transition: transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease;
}

.studio-model-chip:hover,
.satellite-model-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

@media (min-width: 960px) {
  .satellite-col-cc {
    flex: 0 0 29.1667% !important;
    max-width: 29.1667% !important;
  }

  .satellite-col-cj {
    flex: 0 0 41.6667% !important;
    max-width: 41.6667% !important;
  }

  .satellite-col-bog-pei {
    flex: 0 0 29.1667% !important;
    max-width: 29.1667% !important;
  }
}



</style>
